import { FileDownload, Search, ViewColumn } from "@mui/icons-material"
import {
    Box,
    Button,
    Divider,
    IconButton,
    InputAdornment,
    Menu,
    MenuItem,
    Stack,
    TextField,
    Tooltip,
    useMediaQuery,
    useTheme,
} from "@mui/material"
import { ColumnsPanelTrigger, ExportCsv, ExportPrint, Toolbar, ToolbarButton } from "@mui/x-data-grid"
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers"
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3"
import { isValid } from "date-fns"
import { memo, useEffect, useRef, useState } from "react"
import { NexDrawer } from "../../../components/Drawer/Drawer"
import { useNexusNavigate } from "../../../hooks/useNexusNavigate"
import { useNexusSensorIdOptional, useSensorInstallSearchFiltersWithDefaults } from "../../../hooks/useNexusParams"
import { TimeUtilsDateFns } from "../../../utils/TimeUtils"
import { CreateSensorInstallIconButton } from "./CreateSensorInstallIconButton"
import { SensorInstallFiltersAndPaging } from "./SensorInstallSearchFilters"

const filterInputMaxWidth = 240

export const SensorInstallToolbar = memo(() => {
    const sensorId = useNexusSensorIdOptional()
    const currentFiltersWithDefaults = useSensorInstallSearchFiltersWithDefaults()
    const nexusNavigate = useNexusNavigate()

    const theme = useTheme()
    const mediumScreen = useMediaQuery(theme.breakpoints.down("md"))

    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false)
    const [exportMenuOpen, setExportMenuOpen] = useState(false)
    const exportMenuTriggerRef = useRef<HTMLButtonElement>(null)
    const [validOnDateInput, setDateInput] = useState(
        currentFiltersWithDefaults.valid_on_date
            ? TimeUtilsDateFns.getDateFromFormattedDay(currentFiltersWithDefaults.valid_on_date)
            : undefined,
    )
    const [filenameInput, setFilenameInput] = useState(currentFiltersWithDefaults.file_name)
    const [sensorFilterInput, setSensorFilterInput] = useState(currentFiltersWithDefaults.sensor_search)

    useEffect(() => {
        setFilterDrawerOpen(false)
    }, [mediumScreen])

    const handleSearchFilters = () => {
        if (
            filenameInput === currentFiltersWithDefaults.file_name &&
            validOnDateInput === currentFiltersWithDefaults.valid_on_date &&
            sensorFilterInput === currentFiltersWithDefaults.sensor_search
        ) {
            // Nothing changed - don't do anything
            return
        }

        const searchWith: SensorInstallFiltersAndPaging = {
            ...currentFiltersWithDefaults,
            // use || so on empty string the field is not added to the URL
            file_name: filenameInput?.trim() || undefined,
            valid_on_date:
                isValid(validOnDateInput) && validOnDateInput
                    ? TimeUtilsDateFns.formatDate(validOnDateInput)
                    : undefined,
            sensor_search: sensorFilterInput?.trim() || undefined,
            page: 0,
        }
        nexusNavigate.searchSensorInstalls(searchWith, sensorId)
        setFilterDrawerOpen(false)
    }

    const handleClearFilters = () => {
        setDateInput(undefined)
        setFilenameInput(undefined)
        setSensorFilterInput(undefined)
        nexusNavigate.searchSensorInstalls(undefined, sensorId)
        setFilterDrawerOpen(false)
    }

    return (
        <Toolbar style={{ minHeight: 64 }}>
            <CreateSensorInstallIconButton />

            <Tooltip title="Columns">
                <ColumnsPanelTrigger render={<ToolbarButton />}>
                    <ViewColumn fontSize="small" />
                </ColumnsPanelTrigger>
            </Tooltip>

            <Tooltip title="Export">
                <ToolbarButton
                    ref={exportMenuTriggerRef}
                    id="export-menu-trigger"
                    aria-controls="export-menu"
                    aria-haspopup="true"
                    aria-expanded={exportMenuOpen ? "true" : undefined}
                    onClick={() => setExportMenuOpen(true)}
                >
                    <FileDownload fontSize="small" />
                </ToolbarButton>
            </Tooltip>

            <Menu
                id="export-menu"
                anchorEl={exportMenuTriggerRef.current}
                open={exportMenuOpen}
                onClose={() => setExportMenuOpen(false)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                slotProps={{
                    list: {
                        "aria-labelledby": "export-menu-trigger",
                    },
                }}
            >
                <ExportPrint render={<MenuItem />} onClick={() => setExportMenuOpen(false)}>
                    Print
                </ExportPrint>
                <ExportCsv render={<MenuItem />} onClick={() => setExportMenuOpen(false)}>
                    Download as CSV
                </ExportCsv>
            </Menu>

            <Divider orientation="vertical" variant="middle" flexItem sx={{ mx: 0.5 }} />

            {!mediumScreen && (
                <>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <Tooltip title={"Search within range of start and end date"}>
                            <div>
                                <DatePicker
                                    label="Start - End Date"
                                    sx={{ minWidth: 160, mx: 0.5 }}
                                    value={validOnDateInput ?? null}
                                    onChange={(date) => setDateInput(date ?? undefined)}
                                    slotProps={{ field: { clearable: true }, textField: { size: "small" } }}
                                    format={TimeUtilsDateFns.dayFormat}
                                />
                            </div>
                        </Tooltip>
                    </LocalizationProvider>

                    <TextField
                        size={"small"}
                        label={"Sensor"}
                        onChange={(e) => setSensorFilterInput(e.target.value)}
                        value={sensorFilterInput ?? ""}
                        fullWidth={true}
                        sx={{ maxWidth: filterInputMaxWidth, mx: 0.5 }}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position={"start"}>
                                        <Search fontSize={"small"} />
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />

                    <TextField
                        size={"small"}
                        label={"Filename"}
                        onChange={(e) => setFilenameInput(e.target.value)}
                        value={filenameInput ?? ""}
                        fullWidth={true}
                        sx={{ maxWidth: filterInputMaxWidth, mx: 0.5 }}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position={"start"}>
                                        <Search fontSize={"small"} />
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />

                    <Button size="small" variant={"outlined"} onClick={handleSearchFilters}>
                        Search
                    </Button>

                    <Button size="small" color="error" onClick={handleClearFilters}>
                        Clear
                    </Button>
                </>
            )}

            {mediumScreen && (
                <IconButton onClick={() => setFilterDrawerOpen(true)}>
                    <Search />
                </IconButton>
            )}

            <NexDrawer
                open={filterDrawerOpen}
                onClose={() => setFilterDrawerOpen(false)}
                title="Sensor Installation Filters"
                drawerActions={
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Button size="small" color="error" onClick={handleClearFilters}>
                            Clear Filters
                        </Button>
                        <Button size="small" variant={"outlined"} onClick={handleSearchFilters}>
                            Search
                        </Button>
                    </Box>
                }
            >
                <Stack spacing={2} padding={2}>
                    <TextField
                        size={"small"}
                        label={"Search Sensor"}
                        onChange={(e) => setSensorFilterInput(e.target.value)}
                        value={sensorFilterInput ?? ""}
                        fullWidth={true}
                    />

                    <TextField
                        size={"small"}
                        label={"Search Filename"}
                        onChange={(e) => setFilenameInput(e.target.value)}
                        value={filenameInput ?? ""}
                        fullWidth={true}
                    />

                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                            label="Search Start - End Date"
                            value={validOnDateInput ?? null}
                            onChange={(date) => setDateInput(date ?? undefined)}
                            slotProps={{
                                field: { clearable: true },
                                textField: {
                                    size: "small",
                                    helperText: "Search within range of start and end date",
                                    fullWidth: true,
                                },
                            }}
                            format={TimeUtilsDateFns.dayFormat}
                        />
                    </LocalizationProvider>
                </Stack>
            </NexDrawer>
        </Toolbar>
    )
})
